import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, LinearProgress, Chip, Stack, Divider, Button
} from '@mui/material';
import { EmojiEvents, ExpandMore, ExpandLess } from '@mui/icons-material';

export default function RankedClients({ data = [] }) {
  const [visibleCount, setVisibleCount] = useState(5);
  
  // Tri des clients par rang
  const clients = useMemo(() => [...data].sort((a, b) => a.rank - b.rank), [data]);
  
  // Gestion de l'affichage limité
  const displayedClients = clients.slice(0, visibleCount);
  const hasMore = visibleCount < clients.length;

  const StatCell = ({ value, label }) => (
    <Stack alignItems="center" spacing={0} sx={{ minWidth: 55 }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#1e293b' }}>
        {value || 0}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700, color: '#64748b' }}>
        {label}
      </Typography>
    </Stack>
  );

  return (
    <Paper sx={{ p: 0, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)', overflow: 'hidden' }}>
      <Box sx={{ p: 3, bgcolor: '#ffffff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ bgcolor: '#fff7ed', p: 1, borderRadius: 2 }}>
          <EmojiEvents sx={{ color: '#f59e0b', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a' }}>Statistiques Clients </Typography>
          <Typography variant="body2" color="text.secondary">Engagement client : {clients.length} au total</Typography>
        </Box>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 950 }}>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>RANG</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>CLIENT</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>SCORE</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>CONTRIBUTION</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>ACTIVITÉS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedClients.map((c) => (
              <TableRow key={c.client_id} hover sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Chip 
                    label={`#${c.rank}`} 
                    size="small" 
                    sx={{ fontWeight: 800, bgcolor: c.rank <= 3 ? '#fff7ed' : '#f1f5f9', color: c.rank <= 3 ? '#ea580c' : '#475569', borderRadius: 1.5 }} 
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', fontSize: 13, fontWeight: 700, width: 36, height: 36 }}>
                      {c.profile?.prenom?.[0]}{c.profile?.nom?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>{c.profile?.prenom} {c.profile?.nom}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.profile?.email}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={800} sx={{ color: '#0f172a' }}>{c.score.toLocaleString()}</Typography>
                </TableCell>
                <TableCell sx={{ width: 180 }}>
                  <LinearProgress variant="determinate" value={c.contribution_percent} sx={{ height: 6, borderRadius: 3, mb: 0.5, bgcolor: '#e2e8f0' }} />
                  <Typography variant="caption" fontWeight={700}>{c.contribution_percent}%</Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}>
                    <StatCell value={c.activity?.orders} label="Comandes" />
                    <StatCell value={c.activity?.reservations} label="Réservations" />
                    <StatCell value={c.activity?.subscriptions} label="Abonnements" />
                    <StatCell value={c.activity?.videos} label="Vidéos" />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* BOUTON VOIR PLUS / MOINS */}
      {clients.length > 5 && (
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
          <Button 
            variant="text" 
            endIcon={hasMore ? <ExpandMore /> : <ExpandLess />}
            onClick={() => hasMore ? setVisibleCount(clients.length) : setVisibleCount(5)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {hasMore ? `Voir les ${clients.length - visibleCount} autres clients` : 'Réduire la liste'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}